import { unwrapResult } from '@reduxjs/toolkit'
import { useRef } from 'react'
import { confirmCheckout, setEndConfirmTime, useAppDispatch, useCheckoutState, useCheckoutToken, useHasSavedPaymentMethods } from '../../../state'
import { useSaveOfferMutation, useIssueCardMutation, useLazyGetRedirectQuery } from '../../../state/appleGateway/apis'
import { useUserHasApplePaylaterCard } from '../../../state/applePaylater/hooks'
import { useSaveOfferRequest, useIssueCardRequest, useCloseApplePaylater } from '../hooks'
import { sendPurchaseEvent } from '../../../integrations/googletagmanager/utils'

const APPLE_GATEWAY_REDIRECT_PATH = 'checkout/confirm/redirect'

interface FinaliseCheckoutOptions {
  shouldUpdatePreferredPaymentDay?: boolean
  onOAuthAuthorization?: () => Promise<void>
  onSuccess?: () => void
  onFailure?: () => void
}

export const useFinaliseApplePaylaterCheckout = (): { finaliseCheckout: (options?: FinaliseCheckoutOptions) => Promise<void> } => {
  const dispatch = useAppDispatch()
  const { started, checkout, confirmed } = useCheckoutState()
  const token = useCheckoutToken()
  const hasSavedPaymentMethods = useHasSavedPaymentMethods()
  const userHasApplePaylaterCard = useUserHasApplePaylaterCard()
  const saveOfferRequest = useSaveOfferRequest()
  const issueCardRequest = useIssueCardRequest()
  const [saveOffer] = useSaveOfferMutation()
  const [issueCard] = useIssueCardMutation()
  const [triggerGetRedirect] = useLazyGetRedirectQuery()
  const close = useCloseApplePaylater()

  const confirmedRef = useRef(confirmed)
  confirmedRef.current = confirmed

  const finaliseCheckout = async (options: FinaliseCheckoutOptions = {}): Promise<void> => {
    const { shouldUpdatePreferredPaymentDay, onOAuthAuthorization, onSuccess, onFailure } = options

    if (userHasApplePaylaterCard === false && issueCardRequest !== null) {
      await issueCard(issueCardRequest).unwrap()
    }

    if (saveOfferRequest !== null) {
      await saveOffer(saveOfferRequest).unwrap()
    }

    const confirmResponse = unwrapResult(await dispatch(confirmCheckout({ shouldUpdatePreferredPaymentDay })))

    await onOAuthAuthorization?.()

    dispatch(setEndConfirmTime(performance.now()))

    if (confirmResponse.status !== 'FAILURE') {
      const orderAmount = started?.consumerAmount ?? checkout?.amount
      sendPurchaseEvent(token, !hasSavedPaymentMethods, orderAmount)

      onSuccess?.()

      const callbackUrl = confirmResponse.callbackBaseUrl ?? checkout?.merchant.callbackUrl ?? ''
      const queryArgsToUse = confirmResponse.callbackUrlQueryArgs ?? checkout?.merchant.callbackUrlQueryArgs ?? ''
      let fetchedRedirectUrl: string | undefined

      if (callbackUrl.includes(APPLE_GATEWAY_REDIRECT_PATH)) {
        const result = await triggerGetRedirect({
          queryArgs: queryArgsToUse.length > 0 ? queryArgsToUse : undefined,
        }).unwrap()
        fetchedRedirectUrl = result.redirectUrl
      }

      await close(fetchedRedirectUrl, queryArgsToUse)
    } else {
      onFailure?.()
    }
  }

  return { finaliseCheckout }
}
